import React, { useState, useEffect } from 'react'
import { publicApi } from '../utils/api'
import { getDisplaySundayDateStr } from '../utils/sundayDisplay'
import AppBranding from '../components/AppBranding'

const CHURCH_ORDER = { KTC: 0, SSC: 1, WIC: 2, STC: 3 }
const TYPE_ORDER = { SundaySchool: 0, Sermon: 1, Event: 2 }

export default function PublicPage() {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    loadEvents()
  }, [])

  const loadEvents = async () => {
    try {
      setLoading(true)
      const response = await publicApi.getEvents()
      // Flatten events into rows
      const rows = []
      response.data.data.dates.forEach(dateGroup => {
        Object.entries(dateGroup.churches).forEach(([church, entries]) => {
          entries.forEach(entry => {
            rows.push({
              date: dateGroup.date,
              day: dateGroup.day,
              month: dateGroup.month,
              church,
              ...entry
            })
          })
        })
      })
      const displaySunday = getDisplaySundayDateStr()
      const filtered = rows.filter(row => row.date === displaySunday)
      filtered.sort((a, b) => {
        const churchCompare = (CHURCH_ORDER[a.church] ?? 99) - (CHURCH_ORDER[b.church] ?? 99)
        if (churchCompare !== 0) return churchCompare
        return (TYPE_ORDER[a.type] ?? 99) - (TYPE_ORDER[b.type] ?? 99)
      })
      setEvents(filtered)
    } catch (err) {
      console.error('Failed to load events:', err)
      setError('載入失敗')
    } finally {
      setLoading(false)
    }
  }

  const getTypeLabel = (type) => {
    switch (type) {
      case 'Sermon': return '講道'
      case 'SundaySchool': return '主日學'
      case 'Event': return '活動'
      default: return type
    }
  }

  const getChurchLabel = (church) => {
    switch (church) {
      case 'KTC': return '觀塘'
      case 'SSC': return '上水'
      case 'WIC': return '西環'
      case 'STC': return '沙田'
      default: return church
    }
  }

  const getNotesUrl = (fileUrls) => {
    if (!fileUrls?.length) return null
    const file = fileUrls.find(f => f.url) || fileUrls[0]
    return file?.url || null
  }

  if (loading) {
    return (
      <div className="public-page">
        <div className="loading">載入中...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="public-page">
        <div className="error">{error}</div>
      </div>
    )
  }

  return (
    <div className="public-page">
      {/* Header */}
      <header className="header">
        <AppBranding lang="zh" />
      </header>

      {/* Events List */}
      <div className="events-list">
        {/* Header Row */}
        <div className="events-header">
          <div className="col-date">日期</div>
          <div className="col-type">類型</div>
          <div className="col-church">堂會</div>
          <div className="col-title">標題</div>
          <div className="col-speaker">講員</div>
          <div className="col-actions">連結</div>
        </div>

        {events.length === 0 ? (
          <div className="no-events">暫無活動</div>
        ) : (
          events.map((event, index) => {
            const notesUrl = getNotesUrl(event.file_urls)
            return (
            <div key={index} className="event-row">
              <div className="col-date">
                <span className="day">{event.day}</span>
                <span className="month">/{event.month}</span>
              </div>
              <div className="col-type">{getTypeLabel(event.type)}</div>
              <div className="col-church">{getChurchLabel(event.church)}</div>
              <div className="col-title">{event.title}</div>
              <div className="col-speaker">{event.speaker || '-'}</div>
              <div className="col-actions">
                {event.youtube_url ? (
                  <a
                    href={event.youtube_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-action video"
                  >
                    影片
                  </a>
                ) : (
                  <span className="btn-action video disabled">影片</span>
                )}
                {notesUrl ? (
                  <a
                    href={notesUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-action notes"
                  >
                    筆記
                  </a>
                ) : (
                  <span className="btn-action notes disabled">筆記</span>
                )}
              </div>
            </div>
            )
          })
        )}
      </div>

      <style jsx>{`
        .public-page {
          min-height: 100vh;
          background: #1a1a1a;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #e5e5e5;
        }

        .header {
          padding: 14px 16px;
          border-bottom: 1px solid #333;
          background: linear-gradient(180deg, #1a2744 0%, #1a1a1a 100%);
        }

        .header .app-branding-title {
          margin: 0;
          font-size: 1.15rem;
          font-weight: 600;
          color: #e5e5e5;
          text-align: left;
        }

        .header .app-branding-subtitle {
          display: block;
          margin-top: 3px;
          font-size: 0.72rem;
          font-weight: 500;
          color: #a8b0d0;
          letter-spacing: 0.04em;
        }

        .loading, .error, .no-events {
          text-align: center;
          padding: 48px 16px;
          color: #999;
        }

        .error {
          color: #f87171;
        }

        .events-list {
          padding: 8px;
        }

        .events-header {
          background: #333;
          color: #e5e5e5;
          border-radius: 8px;
          margin-bottom: 8px;
          padding: 12px;
          display: grid;
          grid-template-columns: 50px 55px 45px 1fr 50px auto;
          gap: 8px;
          align-items: center;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .event-row {
          background: #2a2a2a;
          border-radius: 8px;
          margin-bottom: 8px;
          padding: 12px;
          display: grid;
          grid-template-columns: 50px 55px 45px 1fr 50px auto;
          gap: 8px;
          align-items: center;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
          font-size: 0.85rem;
        }

        .col-date {
          display: flex;
          align-items: baseline;
          font-weight: 600;
          color: #e5e5e5;
        }

        .col-date .day {
          font-size: 1rem;
        }

        .col-date .month {
          font-size: 0.8rem;
          color: #999;
        }

        .col-type {
          background: rgba(193, 43, 32, 0.12);
          color: var(--accent-primary, #c12b20);
          font-weight: 600;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.75rem;
          text-align: center;
        }

        .col-church {
          font-size: 0.8rem;
          color: #999;
        }

        .col-title {
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #e5e5e5;
        }

        .col-speaker {
          font-size: 0.8rem;
          color: #999;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .col-actions {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
        }

        .btn-action {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 500;
          text-decoration: none;
          text-align: center;
          transition: all 0.2s;
        }

        .btn-action.video {
          background: #dc2626;
          color: white;
        }

        .btn-action.video:hover {
          background: #b91c1c;
        }

        .btn-action.notes {
          background: #ea580c;
          color: white;
        }

        .btn-action.notes:hover {
          background: #c2410c;
        }

        .btn-action.disabled {
          background: #404040;
          color: #666;
          cursor: not-allowed;
        }

        @media (max-width: 480px) {
          .events-header,
          .event-row {
            grid-template-columns: 50px 50px 40px 1fr;
            grid-template-rows: auto auto;
            gap: 4px;
          }

          .events-header .col-title,
          .events-header .col-speaker,
          .events-header .col-actions {
            display: none;
          }

          .col-title {
            grid-column: 1 / -1;
            order: 1;
          }

          .col-speaker {
            grid-column: 3 / -1;
            order: 2;
          }

          .col-actions {
            grid-column: 1 / -1;
            order: 3;
            justify-content: flex-start;
          }

          .col-date, .col-type, .col-church {
            order: 0;
          }
        }
      `}</style>
    </div>
  )
}